import {
    BaseButton,
    ClaimInfo,
    ClaimsBundleBadge,
    DataTablePresenter,
    Hl7MessageType,
    Invoice,
    ITableColumn,
    parseAmount,
    PaymentStatusType,
    Popover,
    PopoverHeader,
    PopoverItem,
    ProviderClaim,
    ProviderClaimEligibility,
    ProviderClaimPayment,
    ProviderClaimTransmission,
    ProviderCoding,
    ProviderCollectionPayment,
    ProviderComment,
    ProviderDocument,
    ProviderInstitutionalClaim,
    ProviderLetter,
    ProviderMail,
    ProviderMessage,
    ProviderNynf3Claim,
    ProviderPayerSubscriber, ProviderPhone,
    ProviderProfessionalClaim,
    ProviderReceiveFax, ProviderSendEmail,
    ProviderSendFax,
    ProviderStatus,
    ProviderSubscriberPatient,
    renderAmount,
    renderDate,
    SupportHL7Message,
    TimelineSegment,
    Transaction,
} from '@enter/core';
import { getNickname } from '@enter/core/src/ui/messaging/util';
import {mdiChevronDown, mdiChevronRight, mdiEmail, mdiPaperclip, mdiLockOpenVariantOutline, mdiLockOutline} from '@mdi/js';
import { ProviderMessagingContext } from 'components';
import { format, formatDistanceToNow } from 'date-fns';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Row } from 'react-table';
import { IDialogContextProps } from 'services';
import Icon from '@mdi/react';
import styled from '@emotion/styled/macro';
import { Tooltip } from '@enter/core';
import { ClaimDetail } from './claim-detail';
import * as S from './claim-timeline-table.styles';
import { CodingDetail } from './coding-detail';
import { CollectionDetail } from './collections-detail';
import { CommentDetail } from './comment-detail';
import { DocumentDetail } from './document-detail';
import { EligibilityDetail } from '@enter/core';
import { HL7MessageDetail } from './hl7-message-detail';
import { InvoiceDetail } from '../billing-timeline-table/invoice-detail';
import { LetterDetail } from './letter-detail';
import { MailDetail } from './mail-detail';
import { PhoneDetail} from "./phone-detail";
import { PaymentDetail } from './payment-detail';
import { ReceiveFaxDetail } from './receive-fax-detail';
import { SendFaxDetail } from './send-fax-detail';
import { StatusDetail } from './status-detail';
import { TransmissionDetail } from './transmission-detail';
import {EmailDetail} from "./email-details";

const StatusIcon = styled(Icon)`
    margin-right: 8px;
    flex-shrink: 0;
`;

const MessageDescription = styled.div`
    display: flex;
    align-items: center;
`;

interface IClaimTimelineTableProps {
    loading: boolean;
    loadingBundle?: boolean;
    claim?: ProviderClaim;
    claimPind?: ProviderProfessionalClaim | ProviderInstitutionalClaim | ProviderNynf3Claim;
    codings?: ProviderCoding[];
    collections?: ProviderCollectionPayment[];
    comments?: ProviderComment[];
    documents?: ProviderDocument[];
    statuses?: ProviderStatus[];
    eligibilities?: ProviderClaimEligibility[];
    hl7Messages?: SupportHL7Message[];
    invoices?: Invoice[];
    letters?: ProviderLetter[];
    mails?: ProviderMail[];
    phones?: ProviderPhone[];
    messages?: ProviderMessage[];
    payments?: ProviderClaimPayment[];
    receiveFaxes?: ProviderReceiveFax[];
    sendFaxes?: ProviderSendFax[];
    transmissions?: ProviderClaimTransmission[];
    transactions?: Transaction[];
    claimBundle?: ProviderClaim[];
    emails?: ProviderSendEmail[];
    payerSubscribers?: ProviderPayerSubscriber[];
    subscriberPatients?: ProviderSubscriberPatient[];
    dialogContext: IDialogContextProps;
    onLoadClaimBundle?: (bundleId: string) => Promise<void>;
}

interface IClaimTimelineTableState {
    loading?: boolean;
}

interface IClaimTimelineEvent {
    id?: string;
    created?: string;
    updated?: string;
    event?: string;
    open?: boolean;
    description?: React.ReactNode;
    Component?: React.ReactNode;
    onClick?: () => void;
    groupedItems?: IClaimTimelineEvent[];
    isExpanded?: boolean;
    type?: string;
}

export const timeSinceLastEvent = (events: { created?: string }[] | undefined): string | undefined => {
    const recent = _.first(events);
    return recent?.created ? formatDistanceToNow(recent.created) : undefined;
};

export const ClaimTimelineTable: React.FC<IClaimTimelineTableProps> = (props: IClaimTimelineTableProps) => {
    const [loading, setLoading] = useState(false);
    const [expandedIds, setExpandedIds] = useState<string[]>([]);
    const [hiddenEvents, setHiddenEvents] = useState<string[]>([]);
    const [bundleClaims, setBundleClaims] = useState<ClaimInfo[]>([]);
    const [isBundleExpanded, setIsBundleExpanded] = useState(false);

    const messagingContext = useContext(ProviderMessagingContext);

    const presenter = useRef(
        new DataTablePresenter<IClaimTimelineEvent>({
            name: 'billing-timeline',
            data: [],
            columns: [],
            filterSchemas: [],
        }),
    ).current;

    const columns: ITableColumn<IClaimTimelineEvent>[] = useMemo(
        () => [
            {
                id: 'segment',
                Cell: ({ cell }): React.ReactElement => (
                    <S.StyledSegment selectable={!!cell.row.original.Component || !!cell.row.original.onClick}>
                        <TimelineSegment
                            start={cell.row.index === 0}
                            end={cell.row.index === _.size(presenter.data) - 1}
                        />
                    </S.StyledSegment>
                ),
                minWidth: 50,
                maxWidth: 50,
            },
            {
                id: 'dateCreated',
                Header: 'Date Created',
                accessor: 'created',
                Cell: ({ cell }): React.ReactElement => (
                    <S.SelectableCell selectable={!!cell.row.original.Component || !!cell.row.original.onClick}>
                        {cell.value ? format(new Date(cell.value), 'MMM dd, yyyy hh:mm a') : ''}
                    </S.SelectableCell>
                ),
                maxWidth: 200,
            },
            {
                id: 'event',
                Header: 'Event',
                accessor: 'event',
                Cell: ({ cell }): React.ReactElement => (
                    <S.SelectableCell selectable={!!cell.row.original.Component || !!cell.row.original.onClick}>
                        <S.StyledEvent>{cell.value}</S.StyledEvent>
                    </S.SelectableCell>
                ),
                maxWidth: 200,
            },
            {
                id: 'description',
                Header: 'Description',
                accessor: 'description',
                Cell: ({ cell }): React.ReactElement => (
                    <S.StyledDescription selectable={!!cell.row.original.Component || !!cell.row.original.onClick}>
                        <S.Description>{cell.value}</S.Description>
                    </S.StyledDescription>
                ),
                maxWidth: 700,
            },
            {
                id: 'type',
                Header: 'Type',
                accessor: 'type',
                Cell: ({ cell }): React.ReactElement => (
                    <S.SelectableCell selectable={!!cell.row.original.Component || !!cell.row.original.onClick}>
                        {cell.value}
                    </S.SelectableCell>
                ),
                maxWidth: 150,
            },
            {
                id: 'selectable',
                Cell: ({ cell }): React.ReactElement | null => {
                    if (!!cell.row.original.Component || !!cell.row.original.onClick) {
                        const path = cell.row.isExpanded ? mdiChevronDown : mdiChevronRight;
                        return (
                            <S.SelectableCell selectable={true}>
                                <S.StyledIcon path={path} />
                            </S.SelectableCell>
                        );
                    }
                    return null;
                },
                minWidth: 50,
                maxWidth: 50,
            },
        ],
        [presenter.data],
    );

    //Bug fix around changing state in a functional constructor
    useEffect(() => {
        presenter.onColumnsUpdate(columns);
    }, [presenter]);


    const renderCaption = (): React.ReactNode | undefined => {
        const timeSince = timeSinceLastEvent(presenter.data);
        if (timeSince) {
            return `Last event: ${timeSince} ago`;
        }
    };

    useEffect(() => {
        const fetchBundleData = async () => {
            const bundleId = props.claim?.claimBundleId;
            if (bundleId && props.claimBundle) {
                const claimData = props.claimBundle
                    .filter(el => el.id !== props.claim?.id)
                    .map(el => ({
                        id: el.id!,
                        chargeAmount: el.chargeAmount,
                        fromDos: el.fromDos,
                    }));
                setBundleClaims(claimData);
            }
        };
        fetchBundleData();
    }, [props.claimBundle, props.claim]);

    const handleToggleBundle = () => {
        if (!isBundleExpanded && bundleClaims.length === 0) {
            const bundleId = props.claim?.claimBundleId;
            if (bundleId) {
                props.onLoadClaimBundle?.(bundleId);
            }
        }
        setIsBundleExpanded(!isBundleExpanded);
    };

    const onRowClick = async (row: Row<IClaimTimelineEvent>) => {
        if (row.original.Component) {
            row.toggleRowExpanded();
        }

        if (row.original.onClick) {
            setLoading(true);

            try {
                await row.original.onClick();
            } catch (err) {
                props.dialogContext.showError(err);
            } finally {
                setLoading(false);
            }
        }
    };

    const renderExpanded = (row: Row<IClaimTimelineEvent>): React.ReactElement => {
        const isLast = row.index === _.size(presenter.data) - 1;
        if (row.isExpanded) {
            return (
                <S.ExpandRow>
                    {isLast && <S.ExpandSegment />}
                    {!isLast && (
                        <S.ExpandSegment>
                            <TimelineSegment start={false} end={false} />
                        </S.ExpandSegment>
                    )}
                    <S.ExpandCell>{row.original.Component}</S.ExpandCell>
                    <S.ExpandSegment />
                </S.ExpandRow>
            );
        }
        return <></>;
    };

    const stepEvents = (): IClaimTimelineEvent[] => {
        const events: IClaimTimelineEvent[] = [];
        const primaryPayerSubscriber = _.first(props.payerSubscribers);
        const primarySubscriberPatient = _.first(props.subscriberPatients);

        const { claim, claimPind } = props;

        if (claim && claimPind) {
            events.push({
                id: 'claim-created',
                created: claimPind.created,
                event: 'Claim Created',
                description: 'Claim created',
                type: 'Enter Admin',
                Component: (
                    <ClaimDetail
                        claimPind={claimPind}
                        primaryPayerSubscriber={primaryPayerSubscriber}
                        primarySubscriberPatient={primarySubscriberPatient}
                    />
                ),
            });
        }

        const transmission = _.find(props.transmissions, 'status');
        if (transmission) {
            events.push({
                id: 'claim-submitted',
                created: transmission.created,
                event: 'Claim Submitted',
                description: 'Claim submitted',
                type: 'Enter Admin',
            });
        }
        const payment = _.first(_.sortBy(props.payments, 'created'));
        if (payment) {
            events.push({
                id: 'claim-received',
                created: _.get(payment, 'created'),
                event: 'Claim Received',
                description: 'Claim received',
                type: 'Enter Admin',
            });
        }
        const items = _.sortBy(_.flatMap(props.statuses, 'items'), 'finalizedDate');
        if (!_.isEmpty(items)) {
            events.push({
                id: 'claim-finalized',
                created: _.get(_.first(items), 'finalizedDate'),
                event: 'Claim Finalized',
                description: 'Claim finalized',
                type: 'Enter Admin',
            });
        }

        return events;
    };

    const transmissionEvents = (): IClaimTimelineEvent[] => {
        const { transmissions } = props;
        return _.map(transmissions, transmission => {
            let description = 'No statuses';
            const lastStatus = transmission.statuses?.[0].status;
            if (lastStatus) {
                description = lastStatus;
            }

            return {
                id: transmission.id,
                created: transmission.created,
                event: `Claim Transmission`,
                description,
                Component: <TransmissionDetail transmission={transmission} />,
                type: 'Enter Admin',
            };
        });
    };

    const emailEvents = (): IClaimTimelineEvent[] => {
        const {emails} = props;
        return _.map(emails, email => emailEvent(email));
    }

    const phoneEvents = (): IClaimTimelineEvent[] => {
        const {phones} = props;
        return _.map(phones, phone => phoneEvent(phone));
    }

    const documentEvents = (): IClaimTimelineEvent[] => {
        const { documents, letters, mails, receiveFaxes, sendFaxes } = props;
        return _.flatMap(documents, document => {
            // NB: each document may map to multiple Letters/Mail/Faxes
            const events: IClaimTimelineEvent[] = [];
            if (document.id) {
                const letter = _.find(letters, { documentId: document.id });
                if (letter) {
                    events.push(letterEvent(letter));
                }
                const mailItem = _.find(mails, { documentId: document.id });
                if (mailItem) {
                    events.push(mailEvent(mailItem));
                }
                const receiveFaxItem = _.find(receiveFaxes, { documentId: document.id });
                if (receiveFaxItem) {
                    events.push(receiveFaxEvent(receiveFaxItem));
                }
                const sendFaxItem = _.find(sendFaxes, { documentId: document.id });
                if (sendFaxItem) {
                    events.push(sendFaxEvent(sendFaxItem));
                }
                if (_.isEmpty(events)) {
                    events.push(documentEvent(document));
                }
            }
            return events;
        });
    };

    function documentEvent(document: ProviderDocument): IClaimTimelineEvent {
        return {
            id: document.id,
            created: document.created,
            event: 'Documents Added',
            description: <Attachment document={document} />,
            Component: <DocumentDetail document={document} />,
            type: 'Enter Admin',
        };
    }

    function emailEvent(email: ProviderSendEmail): IClaimTimelineEvent {
        return {
            id: email.id,
            created: email.created,
            event: 'Email Sent',
            description: <Email email={email} />,
            Component: <EmailDetail email={email} />,
            type: 'Enter Admin',
        };
    }

    function letterEvent(letter: ProviderLetter): IClaimTimelineEvent {
        return {
            id: letter.id,
            created: letter.created,
            event: 'Letter',
            description: `Letter sent ${renderDate(_.get(letter, 'response.sendDate'))}`,
            Component: <LetterDetail letter={letter} />,
            type: 'Enter Admin',
        };
    }

    function mailEvent(mail: ProviderMail): IClaimTimelineEvent {
        return {
            id: mail.id,
            created: mail.created,
            event: 'Mail',
            description: `Mail received ${renderDate(mail.receivedDate)}`,
            Component: <MailDetail mail={mail} />,
            type: 'Enter Admin',
        };
    }

    function phoneEvent(phone: ProviderPhone): IClaimTimelineEvent {
        return {
            id: phone.id,
            created: phone.created,
            event: 'Phone',
            description: 'Payer Phone Call',
            Component: <PhoneDetail phone={phone} />,
            type: 'Enter Admin'
        }
    }
    function receiveFaxEvent(receiveFax: ProviderReceiveFax): IClaimTimelineEvent {
        return {
            id: receiveFax.id,
            created: receiveFax.created,
            event: 'Fax Received',
            description: `Fax received from ${receiveFax.fromE164}`,
            Component: <ReceiveFaxDetail receiveFax={receiveFax} />,
            type: 'Enter Admin',
        };
    }

    function sendFaxEvent(sendFax: ProviderSendFax): IClaimTimelineEvent {
        return {
            id: sendFax.id,
            created: sendFax.created,
            event: 'Fax Sent',
            description: sendFax.toE164 ? `Fax sent to ${sendFax.toE164}` : 'Fax sent',
            Component: <SendFaxDetail sendFax={sendFax} />,
            type: 'Enter Admin',
        };
    }

    function statusEvents(): IClaimTimelineEvent[] {
        const { statuses } = props;
        const events: IClaimTimelineEvent[] = _.map(statuses, status => {
            return {
                id: status.id,
                created: status.created,
                event: 'Status Change',
                description: `${_.size(status.items)} items`,
                Component: <StatusDetail status={status} />,
                type: 'Enter Admin',
            };
        });
        return events;
    }
    function eligibilityEvents(): IClaimTimelineEvent[] {
        const { eligibilities } = props;
        const events: IClaimTimelineEvent[] = _.map(eligibilities, eligibility => {
            return {
                id: eligibility.id,
                created: eligibility.created,
                event: 'Eligibility Verified',
                description: <Eligibility eligibility={eligibility} />,
                Component: <EligibilityDetail eligibility={eligibility} />,
                type: 'Enter Admin',
            };
        });
        return events;
    }
    function hl7MessageEvents(): IClaimTimelineEvent[] {
        const { hl7Messages } = props;
        const events: IClaimTimelineEvent[] = _.map(hl7Messages, hl7Message => {
            const isProviderClaimUpdate = hl7Message.type === Hl7MessageType.JSONPROVIDERPROFESSIONAL 
                || hl7Message.type === Hl7MessageType.JSONPROVIDERINSTITUTIONAL;
            const eventLabel = isProviderClaimUpdate
                ? 'Provider Claim Update'
                : 'Integration Message';
            
            return {
                id: hl7Message.id,
                created: hl7Message.created,
                event: eventLabel,
                description: hl7Message.type?.startsWith('JSON_') 
                    ? undefined 
                    : (hl7Message.type || `Message received ${renderDate(hl7Message.created)}`),
                Component: <HL7MessageDetail hl7Message={hl7Message} />,
                type: 'External System',
            };
        });
        return events;
    }
    const CollectionDescription: React.FunctionComponent<{ collection: ProviderCollectionPayment }> = React.memo(({ collection }) => {
        return (
            <div style={{ width: '100%' }}>
                <AmountDisplayRow label="Collected Amount" value={collection.collectedAmount} />
                <AmountDisplayRow label="Refunded Amount" value={collection.refundedAmount} />
            </div>
        );
    });

    function collectionEvents(): IClaimTimelineEvent[] {
        const { collections } = props;
        const events: IClaimTimelineEvent[] = _.map(collections, collection => {
            return {
                id: collection.id,
                created: collection.created,
                event: 'Patient Payment',
                description: <CollectionDescription collection={collection} />,
                Component: <CollectionDetail collection={collection} subscriberPatients={props.subscriberPatients} />,
                type: 'Enter Admin',
            };
        });
        return events;
    }

    const handleOpenMessage = (messageId?: string) => () => {
        messagingContext.openPanel?.({ claimId: props.claim?.id, messageId });
    };

    function messageEvents(): IClaimTimelineEvent[] {
        const { messages } = props;
        const events: IClaimTimelineEvent[] = _.map(messages, message => {
            const isOpen = message.open ?? false;
            const statusTooltip = isOpen ? 'Open' : 'Closed';
            const statusIcon = isOpen ? mdiLockOpenVariantOutline : mdiLockOutline;

            return {
                id: message.id,
                created: message.created,
                updated: message.updated,
                event: 'Message',
                description: (
                    <MessageDescription>
                        <Tooltip title={<span>{statusTooltip}</span>} direction="top">
                            <StatusIcon path={statusIcon} size={0.8} />
                        </Tooltip>
                        <span>{message.subject}</span>
                    </MessageDescription>
                ),
                onClick: handleOpenMessage(message.id),
                type: message.author?.nickname || message.author?.type || 'Enter Admin',
            };
        });
        return events;
    }
    const AmountDisplayRow = React.memo<{ label: string; value: any }>(({ label, value }) => {
        if (!value) {
            return null;
        }
        return (
            <div style={{ marginBottom: '4px' }}>
                <span style={{ fontWeight: 500 }}>{label}: </span>
                <span>{renderAmount(value)}</span>
            </div>
        );
    });

    const PaymentDescription: React.FunctionComponent<{ payment: ProviderClaimPayment }> = React.memo(({ payment }) => {
        const { payerAmount, patientAmount, chargeAmount, allowedAmount } = payment;
        
        return (
            <div style={{ width: '100%' }}>
                <AmountDisplayRow label="Allowed Amount" value={allowedAmount} />
                {parseAmount(payerAmount) > 0 && (
                    <AmountDisplayRow label="Payer Amount" value={payerAmount} />
                )}
                {parseAmount(patientAmount) > 0 && (
                    <AmountDisplayRow label="Patient Amount" value={patientAmount} />
                )}
                {parseAmount(chargeAmount) > 0 && (
                    <AmountDisplayRow label="Charge Amount" value={chargeAmount} />
                )}
            </div>
        );
    });

    function paymentEvents(): IClaimTimelineEvent[] {
        const { payments } = props;
        const events: IClaimTimelineEvent[] = _.map(payments, payment => {
            const event = payment.status === PaymentStatusType.REVERSALOFPREVIOUSPAYMENT ? 'Reversal' : 'Payer Payment';

            return {
                id: payment.id,
                created: payment.created,
                event,
                description: <PaymentDescription payment={payment} />,
                Component: <PaymentDetail payment={payment} />,
                type: 'Enter Admin',
            };
        });
        return events;
    }

    function invoiceEvents(): IClaimTimelineEvent[] {
        const { invoices, transactions, payments } = props;
        return _.map(invoices, invoice => {
            const invoiceAmount = invoice.amount?.amount || '0';
            return {
                id: invoice.id,
                created: invoice.created,
                event: 'Invoice',
                description: `Invoice: ${renderAmount(invoice.amount)}`,
                Component: <InvoiceDetail invoice={invoice} transactions={transactions || []} payments={payments || []} />,
                type: 'Enter Admin',
            };
        });
    }

    function codingEvents(): IClaimTimelineEvent[] {
        const { codings } = props;
        console.log('codings', codings);
        return _.map(codings, coding => {
            return {
                id: coding.id,
                created: coding.created,
                event: 'Coding',
                description: `Status: ${coding.status}`,
                Component: <CodingDetail coding={coding} />,
                type: 'Enter Admin',
            };
        });
    }

    function commentEvents(): IClaimTimelineEvent[] {
        const { comments } = props;
        return _.map(comments, comment => {
            return {
                id: comment.id,
                created: comment.created,
                event: 'Comment',
                description: `Author: ${getNickname(comment.author) || 'Unknown'}`,
                Component: <CommentDetail comment={comment} />,
                type: 'Enter Admin',
            };
        });
    }

    const renderRowWrapper = (row: Row<IClaimTimelineEvent>, renderRow: React.ReactElement) => {
        const groupedItems = row.original.groupedItems || [];
        const handleExpandEvents = () => {
            if (row.original.id) {
                setExpandedIds(_.uniq([...expandedIds, row.original.id]));
            }
        };

        return (
            <div>
                {renderRow}
                {!_.isEmpty(groupedItems) && (
                    <S.RowGroupWrapper onClick={handleExpandEvents}>
                        There are {groupedItems.length} more similar events. Click to show.
                    </S.RowGroupWrapper>
                )}
            </div>
        );
    };

    const loadData = (): void => {
        if (!props.loading) {
            let events: IClaimTimelineEvent[] = _.flatten([
                stepEvents(),
                transmissionEvents(),
                codingEvents(),
                commentEvents(),
                emailEvents(),
                phoneEvents(),
                documentEvents(),
                statusEvents(),
                eligibilityEvents(),
                hl7MessageEvents(),
                collectionEvents(),
                invoiceEvents(),
                paymentEvents(),
                messageEvents(),
            ]);
            events = _.reverse(_.sortBy(events, (event) => event.updated || event.created));

            events = events.filter(event => event.event && !hiddenEvents.includes(event.event));
            events = getGroupedEvents(events, expandedIds);
            if (!_.isEmpty(events) && !_.isEqual(events, presenter.data)) {
                presenter.onDataUpdate(events);
                presenter.onDataTotalChange(events.length);
            }
        }
    };

    useEffect(() => {
        loadData();
    }, [loadData]);

    const eventTypes = useMemo(() => {
        const allEvents: IClaimTimelineEvent[] = _.flatten([
            stepEvents(),
            transmissionEvents(),
            codingEvents(),
            commentEvents(),
            emailEvents(),
            phoneEvents(),
            documentEvents(),
            statusEvents(),
            eligibilityEvents(),
            hl7MessageEvents(),
            collectionEvents(),
            invoiceEvents(),
            paymentEvents(),
            messageEvents(),
        ]);
        return _(allEvents)
            .map(el => el.event)
            .uniq()
            .sort()
            .compact()
            .value();
    }, [
        props.claim,
        props.claimPind,
        props.transmissions,
        props.codings,
        props.comments,
        props.emails,
        props.phones,
        props.documents,
        props.statuses,
        props.eligibilities,
        props.hl7Messages,
        props.collections,
        props.invoices,
        props.payments,
        props.messages,
    ]);

    const handleChangeHiddenFilters = (filters: string[]) => {
        setHiddenEvents(filters);
    };

    return (
        <S.StyledTitleCard size="large" info="Shows a claims timeline" caption={renderCaption()} title="Claim Timeline">
            {!!props.claim?.claimBundleId && (
                <S.ClaimBundleWrapper>
                    <ClaimsBundleBadge
                        bundleId={props.claim.claimBundleId}
                        claims={bundleClaims}
                        isExpanded={isBundleExpanded}
                        onToggle={handleToggleBundle}
                    />
                </S.ClaimBundleWrapper>
            )}
            {!props.loading && <FilterSelect filters={eventTypes} onChangeHiddenFilters={handleChangeHiddenFilters} />}
            <S.StyledDataTable
                presenter={presenter}
                isLoading={props.loading || loading}
                onRowClick={onRowClick}
                renderRowSubComponent={renderExpanded}
                disableSorting={true}
                hideFiltering={true}
                hideNavigation={true}
                renderRowWrapper={renderRowWrapper}
            />
        </S.StyledTitleCard>
    );
};

const FilterSelect: React.FC<{ filters: string[]; onChangeHiddenFilters: (filters: string[]) => void }> = ({
    filters,
    onChangeHiddenFilters,
}) => {
    const [hiddenFilters, setHiddenFilters] = useState<string[]>(['Comment']);
    useEffect(() => {
        onChangeHiddenFilters(hiddenFilters);
    }, [hiddenFilters]);

    const onFilterToggle = (filter: string) => {
        const newFilters = _.xor(hiddenFilters, [filter]);

        setHiddenFilters(newFilters);
    };

    const renderButton = useCallback(() => {
        let subLabel = '';

        if (hiddenFilters.length === 0) {
            subLabel = 'all';
        } else {
            subLabel = `${filters.length - hiddenFilters.length} of ${filters.length}`;
        }

        return (
            <BaseButton variant="outlined" size="small" type="button">
                Filter events ({subLabel})
            </BaseButton>
        );
    }, [hiddenFilters, filters]);

    const handleToggleAll = () => {
        if (hiddenFilters.length === 0) {
            return setHiddenFilters(filters);
        }

        return setHiddenFilters([]);
    };

    return (
        <S.FilterSelectContainer>
            <Popover renderButton={renderButton} align="right" selectable={true} pinned={true}>
                <PopoverHeader>Select visible events</PopoverHeader>
                {filters.map(filter => {
                    const handleSelectedChange = (): void => onFilterToggle(filter);
                    return (
                        <PopoverItem
                            selected={!hiddenFilters.includes(filter)}
                            key={filter}
                            onClick={handleSelectedChange}>
                            {filter}
                        </PopoverItem>
                    );
                })}
                <PopoverItem selectable={false} onClick={handleToggleAll}>
                    Toggle All
                </PopoverItem>
            </Popover>
        </S.FilterSelectContainer>
    );
};

const Attachment = ({ document }: { document: ProviderDocument }) => (
    <S.Attachment>
        <S.AttachIcon path={mdiPaperclip} size={0.75} key={document.id} />
        <div>{document.filename}</div>
    </S.Attachment>
);

const Email = ({ email }: { email: ProviderSendEmail }) => (
    <S.Attachment>
        <S.AttachIcon path={mdiEmail} size={0.75} key={email.id} />
        <div>{email.subject}</div>
    </S.Attachment>
);

const Eligibility = ({ eligibility }: { eligibility: ProviderClaimEligibility }) => (
    <>
        {eligibility.planId && (
            <S.DescriptionItem key={eligibility.planId}>Plan ID: {eligibility.planId}</S.DescriptionItem>
        )}
        {eligibility.groupId && (
            <S.DescriptionItem key={eligibility.groupId}>Group ID: {eligibility.groupId}</S.DescriptionItem>
        )}
    </>
);

function getGroupedEvents(items: IClaimTimelineEvent[], expandedIds: string[]): IClaimTimelineEvent[] {
    const res: IClaimTimelineEvent[] = [];
    let temp: IClaimTimelineEvent[] = [];

    for (const item of items) {
        const lastItem = _.last(temp);

        // when temp array is empty
        if (!lastItem) {
            temp.push(item);
            continue;
        }

        // build temp array, group the similar events
        if (item.event === lastItem.event) {
            temp.push(item);
        } else {
            addTemp();
            temp = [item];
        }
    }

    function addTemp() {
        const firstItem = _.first(temp);
        if (!firstItem?.id || expandedIds.includes(firstItem.id)) {
            res.push(...temp);
            return;
        }

        if (temp.length > 2) {
            firstItem.groupedItems = [...temp.slice(1)];
            res.push(firstItem);
        } else {
            res.push(...temp);
        }
    }

    // add the rest
    addTemp();

    return res;
}
